import { Coordinate } from '../../types/Site';
import { getSitesWithinRadius } from '../../store/ForNeighborsApi';
import { useForNeighborsSitesFetch, ForNeighborsSitesFetchResult } from './useForNeighborsSitesFetch';

export type UseSitesWithinRadiusResult = ForNeighborsSitesFetchResult;

export function useSitesWithinRadius(
  center: Coordinate | null,
  radiusMeters: number,
  isLoggedIn: boolean,
  enabled: boolean = true
): UseSitesWithinRadiusResult {
  return useForNeighborsSitesFetch(
    enabled && center !== null,
    // Paused (enabled=false) preserves results so they're ready the instant this re-enables;
    // no center at all (nothing to search from) clears them -- the two independent reasons this
    // can be inactive have different "what to show meanwhile" answers.
    !enabled,
    // isLoggedIn mirrors Api.getPantries' isLoggedIn param -- neighbors can browse the map
    // without an account, so this must not unconditionally require a session. The `center as
    // Coordinate` cast is safe: this closure only ever runs (via useForNeighborsSitesFetch) when
    // `active` (enabled && center !== null) is true.
    //
    // GetSitesWithinRadius is the one Sites operation whose response wraps each site in an
    // envelope ({ results: [{ site, distanceMeters }] }) rather than vending a bare Site[]. The
    // envelope's distanceMeters is measured from the search center -- the map viewport, not the
    // neighbor -- so it is deliberately dropped here rather than carried into the app: every
    // distance a neighbor sees is computed client-side from their own resolved location (see
    // useSite.ts's distanceMiles), and keeping a second, differently-anchored distance in play is
    // what previously let one site show two different figures one tap apart.
    async () => {
      const response = await getSitesWithinRadius(
        { origin: { coordinate: center as Coordinate }, radiusMeters },
        isLoggedIn
      );
      return {
        sites: (response.results ?? []).map((result) => result.site),
        resultsTruncated: response.resultsTruncated,
      };
    },
    [center?.latitude, center?.longitude, radiusMeters, isLoggedIn, enabled]
  );
}
